import PersonIcon from '@mui/icons-material/Person';
import { Badge, Box, Button, FormControl, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { GridColDef, GridSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { default as React, useEffect, useState } from 'react';
import Loader from 'src/components/common/Loader';
import { useTab } from 'src/components/hooks/useTab';
import { archLevel } from 'src/data/config';
import { useLazyGetVisitCRFOIDsQuery } from 'src/services/SubjectTrackingDetails';
import { setMessage } from 'src/store/Message';
import { setPath } from 'src/store/Navigation';
import { setBackButtonPTN } from 'src/store/SelectedIntrialPatient';
import { setSelectedSubject, setSubjectInfo } from 'src/store/SelectedSubject';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getBadgeClassName, getStatusMatchedIcon } from './StatusMatchedIcon';
import { useDataReview } from './useDataReview';
import { DataTable } from '@pihealth/common-ui-library';
import { setPreScreenGridRowsData } from 'src/store/preScreenGridRowsData';
import { usePreferedDateFormat } from 'src/util/DateFormatter';

export interface IStudyCRF {
  crf_code: string;
  crf_name: string;
}

const CRFQualityChecks: React.FunctionComponent<any> = () => {
  const apiRef = useGridApiRef();
  const { userPreferedDateFormat } = usePreferedDateFormat();
  const [pageSize, setPageSize] = React.useState(10);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [reviewGridRows, setReviewGridRows] = useState([]);
  const [selectionModel, setSelectionModel] = React.useState<GridSelectionModel>([]);
  const [studyCRFs, setStudyCRFs] = useState<string[]>([]);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const isHierarchyLevelClosed: boolean = useAppSelector(state => state.site.isHierarchyLevelClosed);
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const [gridLoading, setGridLoading] = useState(false);

  const { dataReviewData, dataReviewDataError, dataReviewDataIsLoading, getDataReviewData } = useDataReview();
  const [getVisitsAndCRFOIDs, { isLoading: studyCRFsIsLoading }] = useLazyGetVisitCRFOIDsQuery();
  const [filterObject, setFilterObject] = useState<any>();

  const getDataReviewDataFunc = async (from: number, size: number) => {
    const response: any = await getDataReviewData({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: filterObject,
      application: archLevel === 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
      from,
      size
    });
    if (response.isSuccess) {
      setGridLoading(true);
      const tempRowsData: any = [];
      if (response?.data?.source_data_review.length > 0) {
        let idValue = 1;
        response?.data?.source_data_review?.map((subjectInfo: any) => {
          let subjectFound = false;
          if (tempRowsData?.length > 0) {
            tempRowsData.map((sub: any) => {
              if (subjectInfo.subject.subject_id === sub.subject_id) {
                subjectFound = true;
                if (subjectInfo?.visits.length > 0) {
                  const tempVisits = subjectInfo.visits.map((visit: any) => {
                    return visit.visit_name;
                  });
                  return { ...sub, all_visits: [...sub.visits, ...tempVisits] };
                }
              }
            });
          }
          if (!subjectFound) {
            let tempVisit: { visit_name: string; visit_id: string }[] = [];
            let tempCRFs = {};
            if (subjectInfo.visits.length > 0) {
              tempVisit = subjectInfo.visits.map((visit: any) => {
                return { visit_name: visit.visit_name, visit_id: visit.visit_id };
              });
              if (subjectInfo.visits[0].crfs.length > 0) {
                subjectInfo.visits[0].crfs.map((eachCRF: any) => {
                  tempCRFs = {
                    ...tempCRFs,
                    [eachCRF.crf_OID]: [eachCRF.status, eachCRF.query_status]
                  };
                });
              }
            }
            const tempRow = {
              id: idValue,
              subject_id: subjectInfo.subject.subject_id,
              patient_identifier: subjectInfo.subject.patient_identifier,
              prescreening_id: subjectInfo.subject.prescreening_id,
              ehr_facility_id: subjectInfo.subject.ehr_facility_id,
              all_visits: tempVisit,
              visit_type: tempVisit.length > 0 ? tempVisit[0].visit_name : '',
              visit_id: tempVisit.length > 0 ? tempVisit[0].visit_id : '',
              ...tempCRFs,
              sl_no: subjectInfo.subject.sl_no,
              subject_status: subjectInfo.subject.subject_status,
              crf_activated: subjectInfo.subject.crf_activated
            };
            tempRowsData.push(tempRow);
            idValue = idValue + 1;
          }
        });
      }
      setRowsCount(response.data?.total_records);
      setReviewGridRows(tempRowsData);
      setGridLoading(false);
    }
  };

  useEffect(() => {
    if (!dataReviewDataIsLoading && (pageSize === 10 || pageSize === 20 || pageSize === 30)) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getDataReviewDataFunc(0, pageSize);
      } else {
        getDataReviewDataFunc(pageNumber * pageSize, pageSize);
      }
    }
  }, [pageNumber, pageSize, filterObject]);

  const getCRFsListForStudy = async () => {
    setGridLoading(true);
    const crfsOIDsResponse: any = await getVisitsAndCRFOIDs({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry
    });
    if (crfsOIDsResponse.isSuccess) {
      let studyCRFsList = [];
      if (crfsOIDsResponse.data.length > 0) {
        studyCRFsList = crfsOIDsResponse.data.map((oid: any) => {
          return { OID: oid.OID, custom_name: oid.custom_name };
        });
      }
      setStudyCRFs(studyCRFsList);
    } else if (crfsOIDsResponse.isError) {
      setStudyCRFs([]);
      if (crfsOIDsResponse?.error.status == 500) {
        dispatch(
          setMessage({
            msgDetails: `${crfsOIDsResponse?.error.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (crfsOIDsResponse?.error.status == 403) {
        dispatch(
          setMessage({
            msgDetails: 'Error 403: Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
    setGridLoading(false);
  };
  useEffect(() => {
    (async () => {
      if (studyId !== null) {
        // if (!studyCRFs.length) {  // commented to load CRF OIDs when Study Hierarchy changed
        await getCRFsListForStudy();
        // }
        getDataReviewDataFunc(0, pageSize);
      } else {
        setReviewGridRows([]);
      }
    })();
  }, [selectedSite, selectedCountry, studyId, isHierarchyLevelClosed]);

  useEffect(() => {
    if (currentHierarchyLevel === null) {
      setStudyCRFs([]);
    }
  }, [currentHierarchyLevel]);

  useEffect(() => {
    if (dataReviewDataError) {
      setReviewGridRows([]);
      setRowsCount(0);
    }
  }, [dataReviewDataError]);
  const handleChange = (event: SelectChangeEvent, changedRow: any) => {
    const changedVisitType = event.target.value;
    let changedVisitId = '';
    const updatedGridRows: any = reviewGridRows.map((gridRow: any) => {
      if (gridRow.id === changedRow.id) {
        let updatedCRFs: any = {};
        dataReviewData?.source_data_review?.map((reviewRecord: any) => {
          if (reviewRecord.subject.subject_id === gridRow.subject_id) {
            if (reviewRecord.visits.length > 0) {
              reviewRecord.visits.map((visit: any) => {
                if (visit.visit_name === changedVisitType) {
                  changedVisitId = visit.visit_id;
                  if (visit.crfs.length > 0) {
                    visit.crfs.map((eachCRF: any) => {
                      updatedCRFs = {
                        ...updatedCRFs,
                        [eachCRF.crf_OID]: [eachCRF.status, eachCRF.query_status]
                      };
                    });
                  }
                }
              });
            }
          }
        });
        return {
          id: gridRow.id,
          subject_id: gridRow.subject_id,
          patient_identifier: gridRow.patient_identifier,
          prescreening_id: gridRow.prescreening_id,
          ehr_facility_id: gridRow.ehr_facility_id,
          visit_type: changedVisitType,
          visit_id: changedVisitId,
          all_visits: gridRow.all_visits,
          ...updatedCRFs,
          sl_no: gridRow.sl_no,
          subject_status: gridRow.subject_status,
          crf_activated: gridRow.crf_activated
        };
      } else {
        return gridRow;
      }
    });
    setReviewGridRows(updatedGridRows);
  };
  const studyCRFColumns = studyCRFs.map((eachCRF: any) => {
    const dispalyName = eachCRF?.custom_name ?? eachCRF.OID;
    return {
      field: eachCRF.OID,
      headerName: dispalyName?.toUpperCase(),
      minWidth: dispalyName?.length * 10 + 25,
      renderCell: (params: any) => {
        if (Object.keys(params.row).includes(eachCRF.OID)) {
          return (
            <Tooltip title={params?.row?.[eachCRF.OID]?.[0]} placement="top" className="smalltextTooltip">
              <Button onClick={() => handleStatusButtonClick(params.row, eachCRF.OID)} className="btnlikeIconBtn">
                <Badge
                  classes={{ badge: getBadgeClassName(params.row[eachCRF.OID][1]) }}
                  variant="dot"
                  invisible={params.row[eachCRF.OID][1] === null}
                >
                  {getStatusMatchedIcon(params?.row?.[eachCRF.OID]?.[0])}
                </Badge>
              </Button>
            </Tooltip>
          );
        } else {
          return null;
        }
      },
      valueGetter: (params: any) => {
        if (Object.keys(params.row).includes(eachCRF.OID)) {
          return `${params?.row?.[eachCRF.OID]?.[0]}${
            params?.row?.[eachCRF.OID]?.[1] !== null ? ` : ${params?.row?.[eachCRF.OID]?.[1]}` : ''
          }`;
        }
      },
      customeCSVHeader: `${dispalyName.toUpperCase()} (Event Status : Query Status)`
    };
  });
  const handleSubjectSelect = (row: any) => {
    const subjectInfo = {
      subject_id: row.subject_id,
      visit_type: '',
      selected_crf: '',
      patient_identifier: row.patient_identifier,
      prescreening_id: row.prescreening_id,
      ehr_facility_id: row.ehr_facility_id
    };
    dispatch(setSubjectInfo(subjectInfo));
    dispatch(setSelectedSubject(row.subject_id));
    dispatch(setPreScreenGridRowsData({ ...row, pre_screen_id: row.prescreening_id, sl_no: row.sl_no }));
    dispatch(setBackButtonPTN(true));
    navigateToSubjectTracking();
  };
  const handleStatusButtonClick = (row: any, selectedCRF: string) => {
    if (row.crf_activated) {
      const subjectInfo = {
        subject_id: row.subject_id,
        visit_type: row.visit_type,
        visit_id: row.visit_id,
        selected_crf: selectedCRF,
        patient_identifier: row.patient_identifier,
        prescreening_id: row.prescreening_id,
        ehr_facility_id: row.ehr_facility_id
      };
      dispatch(setSubjectInfo(subjectInfo));
      dispatch(setSelectedSubject(row.subject_id));
      dispatch(setBackButtonPTN(true));
      navigateToSubjectTracking();
    } else {
      dispatch(
        setMessage({
          msgDetails: `CRF’s are not activated for this patient. You will be able to access CRF’s once they are activated in Patient Trial Notebook.`,
          severity: 'warning'
        })
      );
    }
  };
  const reviewGridColumns: GridColDef[] = [
    {
      field: 'subject_id',
      headerName: 'Subject Number',
      minWidth: 140,
      renderCell: (params: any) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.75 }}>
            <Button onClick={() => handleSubjectSelect(params.row)} className="userIconNameBtninTable">
              <PersonIcon sx={{ color: 'rgba(0,0,0,0.5)' }} /> &nbsp;{' '}
              <span style={{ color: 'rgba(0,0,0,0.87)' }}>{params.row.subject_id}</span>
            </Button>
          </Box>
        );
      }
    },
    {
      field: 'visit_type',
      headerName: 'Visit',
      minWidth: 140,
      editable: true,
      renderCell: (params: any) => {
        return (
          <FormControl variant="standard" fullWidth>
            <Select
              labelId="visit_type"
              id="visit_type"
              onChange={e => handleChange(e, params.row)}
              label="visit_type"
              name="visit_type"
              value={params.row.visit_type}
              MenuProps={{ disableScrollLock: true }}
              className="selectDDmenuinTable"
            >
              {params.row.all_visits?.map((visitType: { visit_name: string; visit_id: string }, index: number) => {
                return (
                  <MenuItem key={index} value={visitType.visit_name}>
                    {visitType.visit_name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      }
    },
    ...studyCRFColumns
  ];
  const { setTabVal, tabsData } = useTab();
  const dispatch = useAppDispatch();
  const navigateToSubjectTracking = () => {
    //Adding : separated value to decide which grid data we redirecting from
    dispatch(setPath('dataReview:crfQualityChecks'));
    let currentTabinfo: any = {};
    if (archLevel === 'site') {
      currentTabinfo = tabsData.find((item: any) => item.key === 'patientTrialNoteBook');
    } else {
      currentTabinfo = tabsData.find((item: any) => item.key === 'subjectTracking');
    }
    if (currentTabinfo?.index) {
      setTabVal(currentTabinfo.index);
    }
  };

  const dataTableProps = {
    apiRef,
    onRowsSelection: (newSelection: any) => setSelectionModel(newSelection),
    sx: {
      height: '84.5vh',
      width: '100%',
      '@media (max-width:1367px)': {
        height: '76.5vh'
      }
    },
    filtermode: 'server'
  };

  const filterValues = (data: any) => {
    setFilterObject(data);
  };

  return (
    <div>
      <Loader />
      <DataTable
        dataTableProps={dataTableProps}
        className="datareviewdatagrid"
        rows={reviewGridRows}
        hasDefinedColumns
        columns={reviewGridColumns}
        pagination
        checkboxSelection
        showDateAndTime={false}
        paging
        pageSize={pageSize}
        setPageSize={setPageSize}
        rowsCount={rowsCount}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        paginationMode="server"
        selectionModel={selectionModel}
        datePreference={userPreferedDateFormat}
        loading={gridLoading || studyCRFsIsLoading || dataReviewDataIsLoading}
        exportCSVName={'Conduct CRF Quality Checks'}
        filterValues={(data: any) => filterValues(data)}
        height="76vh"
      />
    </div>
  );
};

export default CRFQualityChecks;
